import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    Building2, Stethoscope, Users, CalendarCheck, IndianRupee, LifeBuoy, SlidersHorizontal, X, RefreshCw,
    AlertTriangle, CheckCircle2, ShieldAlert, LogIn, BrainCircuit, CreditCard, Clock, Hourglass, UserCheck,
    ChevronRight, MessagesSquare, Ticket, TrendingUp, Inbox,
} from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import CommonLoader from '../../components/CommonLoader';
import ReportDateTabs from '../../components/reports/ReportDateTabs';
import useReportDateRange, { DASHBOARD_TABS, parsePeriod } from '../../hooks/useReportDateRange';
import { FilterPanel, FilterSection, FilterOptionList, FilterCountryList, FilterPopover } from '../../components/common/FilterPanel';
import { getPlatformDashboard, getPlatformReportFilters } from '../../redux/platformReportsSlice';
import { canAccess, canViewSupportQueue } from '../../utils/platformPermissions';
import { isQueueScopingOn } from '../../redux/featureSlice';
import { unlessSystemId } from '../../utils/systemId';
import { formatTime } from '../../utils/clock';

// PLATFORM DASHBOARD — the Super Admin's landing page: the whole platform in
// one screen (tenants, doctors, patients, appointments, revenue, support), the
// growth charts for the chosen range, what needs attention, and where to go
// next. Reads GET /api/platform/reports/dashboard (reports.view). The date
// tabs and the country / tenant-type filter re-query; everything on the page
// answers for the same range and filter, so the numbers agree with each other.

const EMPTY_FILTERS = { countryId: '', tenantType: '' };

const fmtNum = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);
const fmtMoney = (amount, currency) => {
    try {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: 0 }).format(Number(amount) || 0);
    } catch {
        return `${currency || ''} ${fmtNum(amount)}`.trim();
    }
};
const fmtPeriod = (period, granularity) => {
    const d = parsePeriod(period);
    if (!d) return String(period ?? '');
    return granularity === 'month'
        ? d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
        : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};
const fmtDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    // "15 Oct, 2:30 PM" — the time of day is 12-hour (utils/clock.js).
    return isNaN(d.getTime()) ? '—' : `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}, ${formatTime(d)}`;
};
const humanise = (s) => String(s || '').replace(/[_-]+/g, ' ');

const TOOLTIP_STYLE = {
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
    padding: '10px 12px',
    fontSize: '12px',
};
const AXIS_TICK = { fontSize: 11, fill: '#6b7280' };

const Card = ({ title, icon: Icon, action, children, testid, className = '' }) => (
    <div data-testid={testid} className={`bg-white rounded-2xl border border-gray-200 shadow-sm p-5 ${className}`}>
        {title && (
            <div className="flex items-center justify-between gap-2 mb-3.5">
                <h2 className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest text-[#2D8A8A]">
                    {Icon && <Icon className="w-3.5 h-3.5" />}
                    {title}
                </h2>
                {action}
            </div>
        )}
        {children}
    </div>
);

const Chip = ({ tone = 'teal', children, testid }) => {
    const tones = {
        teal: 'bg-[#E7F5F4] text-[#2D8A8A] border-[#4ABDBD]/60',
        gold: 'bg-[#FBF1E4] text-[#7A5320] border-[#D7A05C]/70',
        green: 'bg-green-50 text-green-700 border-green-300',
        red: 'bg-red-50 text-red-700 border-red-300',
        gray: 'bg-gray-100 text-gray-500 border-gray-300',
    };
    return (
        <span data-testid={testid} className={`inline-flex items-center gap-1 text-[10px] font-bold border rounded-full px-2 py-0.5 whitespace-nowrap ${tones[tone] || tones.teal}`}>
            {children}
        </span>
    );
};

// A <Link> only when the session may open the destination; otherwise the same
// row as a plain <div>. This page opens on reports.view alone, but its cross-
// links lead into other permission families whose route guards would bounce a
// reports-only staff role straight back here — the "link that opens a page
// that bounces" bug class App.jsx describes. Super Admin passes every check.
const MaybeLink = ({ enabled, to, className, hoverClassName = '', children, ...rest }) => (
    enabled
        ? <Link to={to} className={`${className} ${hoverClassName}`} {...rest}>{children}</Link>
        : <div className={className} {...rest}>{children}</div>
);

const KpiCard = ({ icon: Icon, label, value, sub, children, testid, delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay }}
        data-testid={testid}
        className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 flex flex-col gap-2 min-w-0"
    >
        <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 truncate">{label}</p>
            <span className="flex-none w-8 h-8 rounded-xl bg-gradient-to-br from-[#4ABDBD]/15 to-[#2D8A8A]/15 text-[#2D8A8A] flex items-center justify-center">
                {Icon && <Icon className="w-4 h-4" />}
            </span>
        </div>
        <p className="text-2xl font-bold text-gray-900 leading-none truncate" data-testid={testid ? `${testid}-value` : undefined}>{value}</p>
        {sub && <p className="text-[11px] text-gray-500">{sub}</p>}
        {children && <div className="flex flex-wrap gap-1 mt-0.5">{children}</div>}
    </motion.div>
);

const PlatformDashboard = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { dashboard, dashboardLoading, filters: filterOptions } = useSelector((state) => state.platformReports);
    // Whether support.queue.<queue> still counts — the same source
    // PlatformPermissionRoute reads, so this page and the guard never disagree.
    const queueScopingOn = useSelector(isQueueScopingOn);
    // Presentation-only gates for the cross-family links (see MaybeLink).
    const canSeeTenants = canAccess('tenants.view');
    const canSeeAudit = canAccess('audit.view');
    const canOpenQueue = (queue) => canViewSupportQueue(queue, queueScopingOn);

    const range = useReportDateRange({ defaultTab: 'month' });
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState(EMPTY_FILTERS);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [lastLoaded, setLastLoaded] = useState(null);
    const filterButtonRef = useRef(null);
    const filterRef = useRef(null);

    useEffect(() => {
        dispatch(getPlatformReportFilters());
    }, [dispatch]);

    const load = async () => {
        const r = range.resolveRange();
        if (!r) return;
        const result = await dispatch(getPlatformDashboard({ ...r, countryId: filters.countryId, tenantType: filters.tenantType }));
        if (getPlatformDashboard.rejected.match(result)) {
            toast.error(result.payload?.error || t('platformDashboard.load_failed', 'Failed to load the dashboard.'));
        } else {
            setLastLoaded(new Date());
        }
    };

    // Re-query on every tab / custom date / filter change (and once on mount).
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.activeTab, range.startDate, range.endDate, filters]);

    useEffect(() => {
        if (isFilterOpen) setTempFilters({ ...filters });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFilterOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current && !filterRef.current.contains(event.target) &&
                filterButtonRef.current && !filterButtonRef.current.contains(event.target)
            ) {
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filters]);

    const countries = useMemo(
        () => (filterOptions?.countries || []).map((c) => ({ countryId: c.countryId, countryName: c.name })),
        [filterOptions?.countries]
    );
    const typeLabel = (value) => (filterOptions?.tenantTypes || []).find((x) => x.value === value)?.label || humanise(unlessSystemId(value, ''));
    // Rows carry the human label (`tenantTypeName`) when the API has it.
    const rowTypeLabel = (row) => row?.tenantTypeName || typeLabel(row?.tenantType);
    const countryLabel = (id) => countries.find((c) => c.countryId === id)?.countryName || '—';

    const kpis = dashboard?.kpis;
    const alerts = dashboard?.alerts || {};
    const recent = dashboard?.recent || {};
    const topTenants = dashboard?.topTenants || [];
    const granularity = dashboard?.range?.granularity || 'day';
    const currency = kpis?.revenue?.currency || 'INR';

    const tenantGrowth = (dashboard?.charts?.tenantGrowth || []).map((p) => ({ label: fmtPeriod(p.period, granularity), value: Number(p.count) || 0 }));
    const appointmentsSeries = (dashboard?.charts?.appointments || []).map((p) => ({ label: fmtPeriod(p.period, granularity), value: Number(p.count) || 0 }));
    const revenueSeries = (dashboard?.charts?.revenue || []).map((p) => ({ label: fmtPeriod(p.period, granularity), value: Number(p.amount) || 0 }));

    // Where each alert leads. Only rows with an existing page link — and only
    // when this session may open it; the doctor / subscription ones have no
    // screen in this portal yet.
    const alertRows = [
        { key: 'doctorsPendingReview', icon: UserCheck, label: t('platformDashboard.alert_doctors_pending', 'Doctors pending review') },
        { key: 'subscriptionsExpiringSoon', icon: Hourglass, label: t('platformDashboard.alert_subscriptions_expiring', 'Subscriptions expiring soon') },
        { key: 'subscriptionsInGrace', icon: Clock, label: t('platformDashboard.alert_subscriptions_grace', 'Subscriptions in grace period') },
        { key: 'failedPayments', icon: CreditCard, label: t('platformDashboard.alert_failed_payments', 'Failed payments') },
        { key: 'securityEvents24h', icon: ShieldAlert, label: t('platformDashboard.alert_security_events', 'Security events (24 h)'), to: canSeeAudit ? '/audit/security' : undefined },
        { key: 'failedLogins24h', icon: LogIn, label: t('platformDashboard.alert_failed_logins', 'Failed logins (24 h)'), to: canSeeAudit ? '/audit/login' : undefined },
        { key: 'aiJobFailures24h', icon: BrainCircuit, label: t('platformDashboard.alert_ai_failures', 'AI job failures (24 h)'), to: canSeeAudit ? '/audit/ai' : undefined },
        { key: 'inactiveTenants30d', icon: Building2, label: t('platformDashboard.alert_inactive_tenants', 'Tenants inactive for 30 days'), to: canSeeTenants ? '/tenants' : undefined },
    ];
    const attentionCount = alertRows.filter((row) => (Number(alerts[row.key]) || 0) > 0).length;
    // Backend sends alerts.note when the audit connection is down: the two
    // audit-backed counters are then 0 by construction, not by fact, so the
    // card must say so rather than read "All clear".
    const auditNote = typeof alerts.note === 'string' && alerts.note.trim() ? alerts.note.trim() : null;
    const isAuditRow = (key) => key === 'securityEvents24h' || key === 'failedLogins24h';

    const queuePath = (queue) => (queue === 'patient' ? '/moc/support' : '/platform-support');
    const ticketTone = (status) => {
        const s = String(status || '').toLowerCase();
        if (s === 'closed' || s === 'resolved') return 'gray';
        if (s === 'open' || s === 'new') return 'teal';
        return 'gold';
    };

    const hasActiveFilters = Boolean(filters.countryId || filters.tenantType);
    const showLoader = dashboardLoading && !dashboard;

    const chartEmpty = (testid) => (
        <div className="h-[220px] flex flex-col items-center justify-center text-gray-400" data-testid={testid}>
            <TrendingUp className="w-8 h-8 mb-2 text-gray-300" />
            <p className="text-sm">{t('platformDashboard.no_chart_data', 'No data for this range.')}</p>
        </div>
    );

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-full space-y-4"
            data-testid="platform-dashboard-container"
        >
            {/* Header */}
            <div className="bg-white w-full rounded-2xl p-4 sm:p-6 shadow-md" data-testid="platform-dashboard-header">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-5">
                    <div>
                        <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="platform-dashboard-title">
                            {t('platformDashboard.title', 'Platform Dashboard')}
                        </h3>
                        <p className="mt-1 text-xs text-gray-400" data-testid="platform-dashboard-subtitle">
                            {t('platformDashboard.subtitle', 'Every hospital, doctor and patient on the platform at a glance')}
                            {dashboard?.range?.from && dashboard?.range?.to && (
                                <span className="ml-2 text-gray-500 font-medium">· {dashboard.range.from} → {dashboard.range.to}</span>
                            )}
                            {lastLoaded && (
                                <span className="ml-2">· {t('platformDashboard.updated_at', 'Updated')} {formatTime(lastLoaded)}</span>
                            )}
                        </p>
                    </div>
                </div>

                <ReportDateTabs range={range} tabs={DASHBOARD_TABS} layoutId="activePlatformDashboardTab" testIdPrefix="platform-dashboard">
                    <div className="relative w-full sm:w-auto" data-testid="platform-dashboard-filter-container">
                        <button
                            ref={filterButtonRef}
                            type="button"
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                            className="group flex items-center justify-center gap-2 w-full sm:w-auto h-full px-3 py-2.5 text-white bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                            aria-label={t('reportsCommon.filter', 'Filter')}
                            aria-expanded={isFilterOpen}
                            data-testid="platform-dashboard-filter-button"
                        >
                            <SlidersHorizontal className="w-5 h-5" />
                        </button>
                        {isFilterOpen && (
                            <FilterPopover ref={filterRef}>
                                <FilterPanel
                                    onClear={() => { setTempFilters(EMPTY_FILTERS); setFilters(EMPTY_FILTERS); setIsFilterOpen(false); }}
                                    onApply={() => { setFilters(tempFilters); setIsFilterOpen(false); }}
                                    clearLabel={t('reportsCommon.filter_clear', 'Clear')}
                                    applyLabel={t('reportsCommon.filter_apply', 'Apply')}
                                >
                                    <FilterSection title={t('platformDashboard.filter_tenant_type', 'Tenant type')} isActive={Boolean(tempFilters.tenantType)}>
                                        <FilterOptionList
                                            value={tempFilters.tenantType}
                                            onChange={(val) => setTempFilters({ ...tempFilters, tenantType: val })}
                                            options={filterOptions?.tenantTypes || []}
                                        />
                                    </FilterSection>
                                    <FilterSection title={t('platformDashboard.filter_country', 'Country')} isActive={Boolean(tempFilters.countryId)}>
                                        <FilterCountryList
                                            value={tempFilters.countryId}
                                            onChange={(val) => setTempFilters({ ...tempFilters, countryId: val })}
                                            countries={countries}
                                            searchPlaceholder={t('reportsCommon.search_placeholder', 'Search...')}
                                        />
                                    </FilterSection>
                                </FilterPanel>
                            </FilterPopover>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={load}
                        disabled={dashboardLoading}
                        className="flex items-center justify-center gap-1.5 w-full sm:w-auto px-3 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-[#4ABDBD]/10 hover:text-[#2D8A8A] transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="platform-dashboard-refresh-button"
                    >
                        <RefreshCw className={`w-4 h-4 ${dashboardLoading ? 'animate-spin' : ''}`} />
                        {t('platformDashboard.refresh', 'Refresh')}
                    </button>
                </ReportDateTabs>

                {hasActiveFilters && (
                    <div className="mt-4 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-3 border border-gray-200 shadow-sm" data-testid="platform-dashboard-applied-filters">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold text-gray-700 mr-2">{t('reportsCommon.applied_filters', 'Applied Filters')}:</span>
                            {filters.tenantType && (
                                <div className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                    <span className="text-xs text-gray-600"><span className="font-semibold">{t('platformDashboard.filter_tenant_type', 'Tenant type')}:</span> {typeLabel(filters.tenantType)}</span>
                                    <button type="button" onClick={() => setFilters({ ...filters, tenantType: '' })} className="text-gray-400 hover:text-red-500 cursor-pointer"><X className="h-3.5 w-3.5" /></button>
                                </div>
                            )}
                            {filters.countryId && (
                                <div className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                    <span className="text-xs text-gray-600"><span className="font-semibold">{t('platformDashboard.filter_country', 'Country')}:</span> {countryLabel(filters.countryId)}</span>
                                    <button type="button" onClick={() => setFilters({ ...filters, countryId: '' })} className="text-gray-400 hover:text-red-500 cursor-pointer"><X className="h-3.5 w-3.5" /></button>
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => { setFilters(EMPTY_FILTERS); setTempFilters(EMPTY_FILTERS); }}
                                className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all shadow-sm cursor-pointer"
                            >
                                <X className="h-3.5 w-3.5" />
                                {t('reportsCommon.clear_all', 'Clear All')}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {showLoader && (
                <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl shadow-md" data-testid="platform-dashboard-loading">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('reportsCommon.loading', 'Loading...')}</span>
                </div>
            )}

            {!showLoader && !dashboard && (
                <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl shadow-md text-gray-400" data-testid="platform-dashboard-empty">
                    <Inbox className="w-12 h-12 mb-3 text-gray-300" />
                    <p className="text-base font-medium text-gray-500">{t('platformDashboard.no_data', 'Nothing to show yet.')}</p>
                    <p className="text-sm">{t('platformDashboard.no_data_hint', 'Pick a date range to load the platform numbers.')}</p>
                </div>
            )}

            {dashboard && (
                <div className={`space-y-4 transition-opacity ${dashboardLoading ? 'opacity-60' : ''}`} data-testid="platform-dashboard-body">
                    {/* KPI row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4" data-testid="platform-dashboard-kpis">
                        <KpiCard
                            icon={Building2}
                            label={t('platformDashboard.kpi_tenants', 'Tenants')}
                            value={fmtNum(kpis?.tenants?.total)}
                            sub={`${fmtNum(kpis?.tenants?.active)} ${t('platformDashboard.active', 'active')} · ${fmtNum(kpis?.tenants?.inactive)} ${t('platformDashboard.inactive', 'inactive')} · +${fmtNum(kpis?.tenants?.newInRange)} ${t('platformDashboard.in_range', 'in range')}`}
                            testid="platform-dashboard-kpi-tenants"
                        >
                            {Object.entries(kpis?.tenants?.byType || {}).map(([type, n]) => (
                                <Chip key={type} tone="teal" testid={`platform-dashboard-kpi-tenants-type-${type}`}>{typeLabel(type)} · {fmtNum(n)}</Chip>
                            ))}
                        </KpiCard>
                        <KpiCard
                            icon={Stethoscope}
                            label={t('platformDashboard.kpi_doctors', 'Doctors')}
                            value={fmtNum(kpis?.doctors?.total)}
                            sub={`${fmtNum(kpis?.doctors?.active)} ${t('platformDashboard.active', 'active')} · +${fmtNum(kpis?.doctors?.newInRange)} ${t('platformDashboard.in_range', 'in range')}`}
                            testid="platform-dashboard-kpi-doctors"
                            delay={0.05}
                        >
                            <Chip tone={(Number(kpis?.doctors?.pendingReview) || 0) > 0 ? 'gold' : 'gray'}>
                                {fmtNum(kpis?.doctors?.pendingReview)} {t('platformDashboard.pending_review', 'pending review')}
                            </Chip>
                        </KpiCard>
                        <KpiCard
                            icon={Users}
                            label={t('platformDashboard.kpi_patients', 'Patients')}
                            value={fmtNum(kpis?.patients?.mocAccounts)}
                            sub={`${t('platformDashboard.moc_accounts', 'Mitra One Care accounts')} · +${fmtNum(kpis?.patients?.newInRange)} ${t('platformDashboard.in_range', 'in range')}`}
                            testid="platform-dashboard-kpi-patients"
                            delay={0.1}
                        >
                            <Chip tone="teal">{fmtNum(kpis?.patients?.tenantPatients)} {t('platformDashboard.tenant_patients', 'hospital records')}</Chip>
                        </KpiCard>
                        <KpiCard
                            icon={CalendarCheck}
                            label={t('platformDashboard.kpi_appointments', 'Appointments')}
                            value={fmtNum(kpis?.appointments?.inRange)}
                            sub={`${t('platformDashboard.in_range_label', 'In range')} · ${fmtNum(kpis?.appointments?.today)} ${t('platformDashboard.today_lower', 'today')}`}
                            testid="platform-dashboard-kpi-appointments"
                            delay={0.15}
                        >
                            {Object.entries(kpis?.appointments?.byStatus || {}).map(([status, n]) => (
                                <Chip key={status} tone="gray" testid={`platform-dashboard-kpi-appointments-status-${status}`}>
                                    <span className="capitalize">{humanise(status)}</span> · {fmtNum(n)}
                                </Chip>
                            ))}
                        </KpiCard>
                        <KpiCard
                            icon={IndianRupee}
                            label={t('platformDashboard.kpi_revenue', 'Revenue')}
                            value={fmtMoney(kpis?.revenue?.inRange, currency)}
                            sub={`${fmtNum(kpis?.revenue?.paymentsCount)} ${t('platformDashboard.payments', 'payments')} ${t('platformDashboard.in_range', 'in range')}`}
                            testid="platform-dashboard-kpi-revenue"
                            delay={0.2}
                        />
                        <KpiCard
                            icon={LifeBuoy}
                            label={t('platformDashboard.kpi_support', 'Support')}
                            value={fmtNum(kpis?.support?.open)}
                            sub={`${t('platformDashboard.open_tickets', 'Open tickets')} · ${fmtNum(kpis?.support?.closedInRange)} ${t('platformDashboard.closed_in_range', 'closed in range')}`}
                            testid="platform-dashboard-kpi-support"
                            delay={0.25}
                        >
                            <Chip tone={(Number(kpis?.support?.slaBreached) || 0) > 0 ? 'red' : 'green'}>
                                {fmtNum(kpis?.support?.slaBreached)} {t('platformDashboard.sla_breached', 'SLA breached')}
                            </Chip>
                        </KpiCard>
                    </div>

                    {/* Charts row */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="platform-dashboard-charts">
                        <Card title={t('platformDashboard.chart_tenant_growth', 'Tenant growth')} icon={Building2} testid="platform-dashboard-chart-tenants">
                            {tenantGrowth.length === 0 ? chartEmpty('platform-dashboard-chart-tenants-empty') : (
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={tenantGrowth} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
                                        <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
                                        <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
                                        <Tooltip cursor={{ fill: 'rgba(74, 189, 189, 0.1)' }} contentStyle={TOOLTIP_STYLE} labelStyle={{ fontWeight: 'bold', color: '#2D8A8A' }} formatter={(value) => [fmtNum(value), t('platformDashboard.new_tenants', 'New tenants')]} />
                                        <Bar dataKey="value" fill="#4ABDBD" radius={[6, 6, 0, 0]} barSize={18} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </Card>
                        <Card title={t('platformDashboard.chart_appointments_scheduled', 'Appointments (by scheduled date)')} icon={CalendarCheck} testid="platform-dashboard-chart-appointments">
                            {appointmentsSeries.length === 0 ? chartEmpty('platform-dashboard-chart-appointments-empty') : (
                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart data={appointmentsSeries} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
                                        <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
                                        <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
                                        <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ fontWeight: 'bold', color: '#2D8A8A' }} formatter={(value) => [fmtNum(value), t('platformDashboard.chart_appointments', 'Appointments')]} />
                                        <Line type="monotone" dataKey="value" stroke="#2D8A8A" strokeWidth={2} dot={{ r: 3, fill: '#2D8A8A', strokeWidth: 0 }} activeDot={{ r: 5 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </Card>
                        <Card title={`${t('platformDashboard.chart_revenue', 'Revenue')} (${currency})`} icon={IndianRupee} testid="platform-dashboard-chart-revenue">
                            {revenueSeries.length === 0 ? chartEmpty('platform-dashboard-chart-revenue-empty') : (
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={revenueSeries} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
                                        <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
                                        <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={(v) => new Intl.NumberFormat('en-IN', { notation: 'compact' }).format(v)} />
                                        <Tooltip cursor={{ fill: 'rgba(215, 160, 92, 0.12)' }} contentStyle={TOOLTIP_STYLE} labelStyle={{ fontWeight: 'bold', color: '#7A5320' }} formatter={(value) => [fmtMoney(value, currency), t('platformDashboard.chart_revenue', 'Revenue')]} />
                                        <Bar dataKey="value" fill="#D7A05C" radius={[6, 6, 0, 0]} barSize={18} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </Card>
                    </div>

                    {/* Attention + support */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <Card
                            className="lg:col-span-2"
                            title={t('platformDashboard.needs_attention', 'Needs attention')}
                            icon={AlertTriangle}
                            testid="platform-dashboard-alerts"
                            action={auditNote && attentionCount === 0
                                ? <Chip tone="gold"><AlertTriangle className="w-3 h-3" /> {t('platformDashboard.partial_data', 'Partial data')}</Chip>
                                : attentionCount === 0
                                    ? <Chip tone="green"><CheckCircle2 className="w-3 h-3" /> {t('platformDashboard.all_clear', 'All clear')}</Chip>
                                    : <Chip tone="gold">{attentionCount} {t('platformDashboard.items', 'items')}</Chip>}
                        >
                            {auditNote && (
                                <p
                                    data-testid="platform-dashboard-alerts-note"
                                    className="mb-2 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800"
                                >
                                    <AlertTriangle className="w-4 h-4 flex-none" />
                                    <span>{auditNote}</span>
                                </p>
                            )}
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {alertRows.map((row) => {
                                    const n = Number(alerts[row.key]) || 0;
                                    const Icon = row.icon;
                                    // A 0 the server did not measure is shown as unknown, not zero.
                                    const unavailable = Boolean(auditNote) && isAuditRow(row.key);
                                    const inner = (
                                        <>
                                            <span className={`flex-none w-8 h-8 rounded-xl flex items-center justify-center ${n > 0 ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-400'}`}>
                                                <Icon className="w-4 h-4" />
                                            </span>
                                            <span className="flex-1 min-w-0 text-sm text-gray-700 truncate">{row.label}</span>
                                            <span className={`text-sm font-bold tabular-nums ${n > 0 ? 'text-amber-700' : 'text-gray-400'}`}>{unavailable ? '—' : fmtNum(n)}</span>
                                            {row.to && <ChevronRight className="w-4 h-4 text-gray-300 flex-none" />}
                                        </>
                                    );
                                    const cls = `flex items-center gap-3 rounded-xl border px-3 py-2 ${n > 0 ? 'border-amber-200 bg-amber-50/40' : 'border-gray-100 bg-gray-50/50'} ${row.to ? 'hover:border-[#4ABDBD] hover:bg-[#4ABDBD]/5 transition-colors' : ''}`;
                                    return (
                                        <li key={row.key} data-testid={`platform-dashboard-alert-${row.key}`}>
                                            {row.to ? <Link to={row.to} className={cls}>{inner}</Link> : <div className={cls}>{inner}</div>}
                                        </li>
                                    );
                                })}
                            </ul>
                        </Card>

                        <Card title={t('platformDashboard.support_widget', 'Support queues')} icon={LifeBuoy} testid="platform-dashboard-support">
                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <div className="rounded-xl bg-gray-50 border border-gray-100 p-3">
                                    <p className="text-[11px] font-semibold text-gray-500">{t('platformDashboard.open_tickets', 'Open tickets')}</p>
                                    <p className="text-xl font-bold text-gray-900">{fmtNum(kpis?.support?.open)}</p>
                                </div>
                                <div className={`rounded-xl border p-3 ${(Number(kpis?.support?.slaBreached) || 0) > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-100'}`}>
                                    <p className="text-[11px] font-semibold text-gray-500">{t('platformDashboard.sla_breached_title', 'SLA breached')}</p>
                                    <p className={`text-xl font-bold ${(Number(kpis?.support?.slaBreached) || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`} data-testid="platform-dashboard-support-sla-breached">{fmtNum(kpis?.support?.slaBreached)}</p>
                                </div>
                            </div>
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{t('platformDashboard.unassigned', 'Unassigned')}</p>
                            <div className="space-y-2">
                                <MaybeLink enabled={canOpenQueue('patient')} to="/moc/support" className="flex items-center gap-3 rounded-xl border border-gray-100 px-3 py-2" hoverClassName="hover:border-[#4ABDBD] hover:bg-[#4ABDBD]/5 transition-colors" data-testid="platform-dashboard-support-patient-link">
                                    <MessagesSquare className="w-4 h-4 text-[#2D8A8A] flex-none" />
                                    <span className="flex-1 text-sm text-gray-700">{t('platformDashboard.patient_queue', 'Patient Support')}</span>
                                    <span className="text-sm font-bold text-gray-900 tabular-nums">{fmtNum(kpis?.support?.unassigned?.patient)}</span>
                                    {canOpenQueue('patient') && <ChevronRight className="w-4 h-4 text-gray-300" />}
                                </MaybeLink>
                                <MaybeLink enabled={canOpenQueue('tenant')} to="/platform-support" className="flex items-center gap-3 rounded-xl border border-gray-100 px-3 py-2" hoverClassName="hover:border-[#4ABDBD] hover:bg-[#4ABDBD]/5 transition-colors" data-testid="platform-dashboard-support-tenant-link">
                                    <LifeBuoy className="w-4 h-4 text-[#2D8A8A] flex-none" />
                                    <span className="flex-1 text-sm text-gray-700">{t('platformDashboard.tenant_queue', 'Tenant Support')}</span>
                                    <span className="text-sm font-bold text-gray-900 tabular-nums">{fmtNum(kpis?.support?.unassigned?.tenant)}</span>
                                    {canOpenQueue('tenant') && <ChevronRight className="w-4 h-4 text-gray-300" />}
                                </MaybeLink>
                            </div>
                            <p className="mt-3 text-[11px] text-gray-500">{fmtNum(kpis?.support?.closedInRange)} {t('platformDashboard.closed_in_range', 'closed in range')}</p>
                        </Card>
                    </div>

                    {/* Top tenants + recent */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <Card
                            className="lg:col-span-2"
                            title={t('platformDashboard.top_tenants', 'Top tenants in range')}
                            icon={TrendingUp}
                            testid="platform-dashboard-top-tenants"
                            action={<Link to="/tenant-report" className="text-xs font-semibold text-[#2D8A8A] hover:underline">{t('platformDashboard.full_report', 'Full report')}</Link>}
                        >
                            {topTenants.length === 0 ? (
                                <p className="py-8 text-center text-sm text-gray-400">{t('platformDashboard.no_top_tenants', 'No tenant activity in this range.')}</p>
                            ) : (
                                <div className="overflow-x-auto rounded-md border border-gray-200">
                                    <table className="w-full border-collapse">
                                        <thead>
                                            <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                                <th className="px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider">{t('platformDashboard.col_tenant', 'Tenant')}</th>
                                                <th className="px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider">{t('platformDashboard.col_type', 'Type')}</th>
                                                <th className="px-3 py-2.5 text-right text-[11px] font-bold uppercase tracking-wider">{t('platformDashboard.col_appointments', 'Appointments')}</th>
                                                <th className="px-3 py-2.5 text-right text-[11px] font-bold uppercase tracking-wider">{t('platformDashboard.col_patients', 'Patients')}</th>
                                                <th className="px-3 py-2.5 text-right text-[11px] font-bold uppercase tracking-wider">{t('platformDashboard.col_revenue', 'Revenue')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {topTenants.map((row, index) => (
                                                <tr
                                                    key={row.tenantId || index}
                                                    onClick={canSeeTenants && row.tenantId ? () => navigate(`/tenants/${row.tenantId}/view`) : undefined}
                                                    className={`border-b border-gray-100/50 transition-all ${canSeeTenants ? 'hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 cursor-pointer' : ''}`}
                                                    data-testid={`platform-dashboard-top-tenant-${index}`}
                                                >
                                                    <td className="px-3 py-3 text-xs">
                                                        <p className="font-medium text-gray-800 truncate max-w-[220px]">{row.name || t('moc.hr_detail_hospital', 'Hospital')}</p>
                                                    </td>
                                                    <td className="px-3 py-3 text-xs text-gray-600">{rowTypeLabel(row)}</td>
                                                    <td className="px-3 py-3 text-xs text-right text-gray-700 tabular-nums">{fmtNum(row.appointments)}</td>
                                                    <td className="px-3 py-3 text-xs text-right text-gray-700 tabular-nums">{fmtNum(row.patients)}</td>
                                                    <td className="px-3 py-3 text-xs text-right font-semibold text-gray-800 tabular-nums">{fmtMoney(row.revenue, currency)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </Card>

                        <div className="space-y-4">
                            <Card
                                title={t('platformDashboard.recent_tenants', 'Recent tenants')}
                                icon={Building2}
                                testid="platform-dashboard-recent-tenants"
                                action={canSeeTenants ? <Link to="/tenants" className="text-xs font-semibold text-[#2D8A8A] hover:underline">{t('platformDashboard.view_all', 'View all')}</Link> : null}
                            >
                                {(recent.tenants || []).length === 0 ? (
                                    <p className="py-4 text-center text-sm text-gray-400">{t('platformDashboard.no_recent_tenants', 'No tenants created recently.')}</p>
                                ) : (
                                    <ul className="divide-y divide-gray-100">
                                        {recent.tenants.map((tenant, index) => (
                                            <li key={tenant.tenantId || index}>
                                                {canSeeTenants ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => tenant.tenantId && navigate(`/tenants/${tenant.tenantId}/view`)}
                                                        className="w-full flex items-center gap-3 py-2 text-left hover:bg-gray-50 rounded-lg px-1 cursor-pointer"
                                                        data-testid={`platform-dashboard-recent-tenant-${index}`}
                                                    >
                                                        <span className={`w-2 h-2 rounded-full flex-none ${tenant.status === 'active' ? 'bg-green-500' : 'bg-gray-300'}`} />
                                                        <span className="flex-1 min-w-0">
                                                            <span className="block text-sm text-gray-800 truncate">{tenant.name || t('moc.hr_detail_hospital', 'Hospital')}</span>
                                                            <span className="block text-[11px] text-gray-400">{rowTypeLabel(tenant)} · {fmtDateTime(tenant.createdAt)}</span>
                                                        </span>
                                                        <ChevronRight className="w-4 h-4 text-gray-300 flex-none" />
                                                    </button>
                                                ) : (
                                                    <div className="w-full flex items-center gap-3 py-2 text-left rounded-lg px-1" data-testid={`platform-dashboard-recent-tenant-${index}`}>
                                                        <span className={`w-2 h-2 rounded-full flex-none ${tenant.status === 'active' ? 'bg-green-500' : 'bg-gray-300'}`} />
                                                        <span className="flex-1 min-w-0">
                                                            <span className="block text-sm text-gray-800 truncate">{tenant.name || t('moc.hr_detail_hospital', 'Hospital')}</span>
                                                            <span className="block text-[11px] text-gray-400">{rowTypeLabel(tenant)} · {fmtDateTime(tenant.createdAt)}</span>
                                                        </span>
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Card>

                            <Card title={t('platformDashboard.recent_tickets', 'Recent tickets')} icon={Ticket} testid="platform-dashboard-recent-tickets">
                                {(recent.tickets || []).length === 0 ? (
                                    <p className="py-4 text-center text-sm text-gray-400">{t('platformDashboard.no_recent_tickets', 'No tickets raised recently.')}</p>
                                ) : (
                                    <ul className="divide-y divide-gray-100">
                                        {recent.tickets.map((ticket, index) => (
                                            <li key={ticket.ticketId || index}>
                                                <MaybeLink
                                                    enabled={canOpenQueue(ticket.queue)}
                                                    to={queuePath(ticket.queue)}
                                                    className="flex items-center gap-3 py-2 rounded-lg px-1"
                                                    hoverClassName="hover:bg-gray-50"
                                                    data-testid={`platform-dashboard-recent-ticket-${index}`}
                                                >
                                                    <span className="flex-1 min-w-0">
                                                        <span className="block text-sm text-gray-800 truncate">{ticket.subject || ticket.ticketId}</span>
                                                        <span className="block text-[11px] text-gray-400">
                                                            {ticket.queue === 'patient' ? t('platformDashboard.patient_queue', 'Patient Support') : t('platformDashboard.tenant_queue', 'Tenant Support')}
                                                            {ticket.tenantName ? <span> · {ticket.tenantName}</span> : null}
                                                            {' · '}{fmtDateTime(ticket.createdAt)}
                                                        </span>
                                                    </span>
                                                    <Chip tone={ticketTone(ticket.status)}><span className="capitalize">{humanise(ticket.status)}</span></Chip>
                                                </MaybeLink>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Card>
                        </div>
                    </div>
                </div>
            )}
        </motion.div>
    );
};

export default PlatformDashboard;
